import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

interface SidebarState {
  collapsed: boolean;
  activeGroup: string | null;
  isAdminMode: boolean;
  toggle: () => void;
  setActiveGroup: (group: string | null) => void;
}

const SidebarContext = createContext<SidebarState | null>(null);

const STORAGE_KEY = 'sidebar-collapsed';

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  const isAdminMode = location.pathname.startsWith('/admin');

  const toggle = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ collapsed, activeGroup, isAdminMode, toggle, setActiveGroup }),
    [collapsed, activeGroup, isAdminMode, toggle, setActiveGroup],
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
}
